// ABOUTME: Hook to check subscription status and determine if blocking modal should be shown
// ABOUTME: Handles admin bypass, route detection, status checking, and API error handling

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionAPI } from '../services/api';
import { SubscriptionStatus } from '../types/api';

export type LockoutReason = 'trial_expired' | 'payment_failed' | 'refunded' | 'emergency_lockout' | null;

interface UseSubscriptionCheckResult {
  shouldShowModal: boolean;
  lockoutReason: LockoutReason;
  isLoading: boolean;
}

export const useSubscriptionCheck = (): UseSubscriptionCheckResult => {
  const { user } = useAuth();
  const location = useLocation();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [isLockedOut, setIsLockedOut] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkSubscription = async () => {
      // Don't check if user is not logged in
      if (!user) {
        setIsLoading(false);
        return;
      }

      // Admins always have access
      if (user.isAdmin) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await subscriptionAPI.getSubscriptionStatus();
        setSubscriptionStatus(response.status);
        setIsLockedOut(response.isLockedOut === true);
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        // On error, don't show modal (fail open)
        setSubscriptionStatus(null);
        setIsLockedOut(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscription();
  }, [user]);

  // Determine if modal should be shown
  const shouldShowModal = (): boolean => {
    // Never show for non-logged-in users or admins
    if (!user || user.isAdmin) {
      return false;
    }

    // Emergency lockout blocks access everywhere (no exception for MyFamilyPage)
    if (isLockedOut) {
      return true;
    }

    // Don't show on MyFamilyPage
    if (location.pathname === '/family') {
      return false;
    }

    // Show modal if subscription is expired, past_due, or refunded
    if (subscriptionStatus === 'expired' || subscriptionStatus === 'past_due' || subscriptionStatus === 'refunded') {
      return true;
    }

    return false;
  };

  // Determine lockout reason
  const getLockoutReason = (): LockoutReason => {
    if (!shouldShowModal()) {
      return null;
    }

    // Emergency lockout takes priority
    if (isLockedOut) {
      return 'emergency_lockout';
    }

    if (subscriptionStatus === 'expired') {
      return 'trial_expired';
    }

    if (subscriptionStatus === 'past_due') {
      return 'payment_failed';
    }

    if (subscriptionStatus === 'refunded') {
      return 'refunded';
    }

    return null;
  };

  return {
    shouldShowModal: shouldShowModal(),
    lockoutReason: getLockoutReason(),
    isLoading,
  };
};
